import { forwardRef, useId } from "react";
import { DatePicker } from "@/components/ui/date-picker";
import { cn } from "@/lib/utils";
import {
  createDateChangeEvent,
  formatDisplayDate,
} from "@/lib/dateInput";
import type { InputProps } from "./Input";

interface DateInputProps extends Omit<InputProps, "type"> {
  loading?: boolean;
  placeholder?: string;
  popoverModal?: boolean;
}

const variantStyles = {
  primary: {
    label: "block text-xs uppercase tracking-widest text-primary-600 dark:text-primary-400 mb-2",
    trigger:
      "rounded-none border-primary-200 bg-white font-light text-primary-950 hover:bg-primary-50 dark:border-primary-800 dark:bg-primary-950 dark:text-white dark:hover:bg-primary-900",
    error: "mt-1 text-sm text-red-600 dark:text-red-400",
  },
  secondary: {
    label: "block text-xs uppercase tracking-widest text-primary-600 dark:text-primary-400 mb-2",
    trigger:
      "rounded-none border-primary-200 bg-white font-light text-primary-950 hover:bg-primary-50 dark:border-primary-800 dark:bg-primary-950 dark:text-white dark:hover:bg-primary-900",
    error: "mt-1 text-sm text-red-600 dark:text-red-400",
  },
};

const Skeleton = ({ className }: { className: string }) => (
  <div className={`animate-pulse rounded-none bg-primary-100 dark:bg-primary-800 ${className}`} />
);

export const DateInput = forwardRef<HTMLButtonElement, DateInputProps>(
  (
    {
      label,
      error,
      variant = "primary",
      fullWidth = true,
      loading = false,
      id,
      value = "",
      onChange,
      disabled,
      required,
      placeholder = "Selecione uma data",
      popoverModal = false,
      className,
    },
    ref
  ) => {
    const generatedId = useId();
    const inputId = id || generatedId;
    const styles = variantStyles[variant];
    const triggerLabel = label
      ? `${label}: ${value ? formatDisplayDate(String(value)) : placeholder}`
      : value
        ? formatDisplayDate(String(value))
        : placeholder;

    if (loading) {
      return (
        <div className={fullWidth ? "w-full" : undefined}>
          {label && (
            <Skeleton
              className={variant === "primary" ? "mb-2 h-4 w-24" : "mb-2 h-5 w-32"}
            />
          )}
          <Skeleton className="h-12 w-full" />
          {error && <Skeleton className="mt-1 h-4 w-48" />}
        </div>
      );
    }

    return (
      <div className={cn(fullWidth ? "w-full" : undefined, className)}>
        {label && (
          <label htmlFor={inputId} className={styles.label}>
            {label}
            {required ? " *" : ""}
          </label>
        )}
        <DatePicker
          ref={ref}
          id={inputId}
          value={String(value)}
          onChange={(isoDate) => onChange?.(createDateChangeEvent(isoDate))}
          disabled={disabled}
          placeholder={placeholder}
          modal={popoverModal}
          aria-label={triggerLabel}
          aria-required={required}
          aria-invalid={!!error}
          triggerClassName={cn(styles.trigger, !fullWidth && "w-auto")}
        />
        {error && <p className={styles.error}>{error}</p>}
      </div>
    );
  }
);

DateInput.displayName = "DateInput";
